import { useState } from 'react';
import TextField from '@mui/material/TextField';
import {
  Accordion, AccordionSummary, AccordionDetails, Typography, Grid, Box,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { MuiFileInput } from 'mui-file-input';
import InfoIcon from '@mui/icons-material/Info';
import DriverIcon from '@mui/icons-material/Description';
import EditItemView from './components/EditItemView';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import { useTranslation } from '../common/components/LocalizationProvider';
import SettingsMenu from './components/SettingsMenu';
import useSettingsStyles from './common/useSettingsStyles';
import useDriverAttributes from '../common/attributes/useDriverAttributes';

const DriverPage = () => {
  const { classes } = useSettingsStyles();
  const t = useTranslation();

  const [item, setItem] = useState();

  // Função para filtrar atributos complexos (objetos)
  const filterAttributes = (attributes) => {
    if (!attributes) return {};

    const filteredAttributes = {};
    Object.keys(attributes).forEach(key => {
      const value = attributes[key];
      // Verifica se o valor não é um objeto complexo ou é null
      if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
        filteredAttributes[key] = value;
      }
    });

    return filteredAttributes;
  };

  // Ensure form object exists in attributes
  const ensureFormStructure = (currentItem) => {
    if (!currentItem) return currentItem;

    return {
      ...currentItem,
      attributes: {
        ...currentItem.attributes,
        form: currentItem.attributes?.form || {}
      }
    };
  };

  const validate = () => item && item.name && item.uniqueId;

  return (
    <EditItemView
      endpoint="drivers"
      item={item}
      setItem={(newItem) => setItem(ensureFormStructure(newItem))}
      validate={validate}
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedDriver']}
    >
      {item && (
        <>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <NewReleasesIcon color="white" />
                <Typography variant="subtitle1">
                  {t('sharedRequired')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box
                display="flex"
                width="100%"
                gap={2}
                sx={{
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: 'flex-start'
                }}
              >
                {/* Campo Nome - Ocupa 60% do espaço */}
                <Box flex={2} minWidth={0} width="100%">
                  <TextField
                    fullWidth
                    value={item.name || ''}
                    onChange={(event) => setItem({ ...item, name: event.target.value })}
                    label={t('sharedName')}
                    required
                    variant="outlined"
                    size="small"
                  />
                </Box>

                {/* Campo Identificador Único - Ocupa 40% do espaço */}
                <Box flex={1} minWidth={0} width="100%">
                  <TextField
                    fullWidth
                    value={item.uniqueId || ''}
                    onChange={(event) => setItem({ ...item, uniqueId: event.target.value })}
                    label={t('sharedDriver')}
                    required
                    variant="outlined"
                    size="small"
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <InfoIcon color="white" />
                <Typography variant="subtitle1">
                  {t('sharedPreferences')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Linha 1 - Informações pessoais */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  {/* Email */}
                  <Box flex={2} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.email || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, email: e.target.value }
                        }
                      })}
                      label={t('sharedEmail')}
                      type="email"
                    />
                  </Box>

                  {/* CPF/Identificação */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.taxId || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, taxId: e.target.value }
                        }
                      })}
                      label={t('attributeItin')}
                    />
                  </Box>

                  {/* Telefone */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.phone || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, phone: e.target.value }
                        }
                      })}
                      label={t('sharedPhone')}
                      type="tel"
                    />
                  </Box>
                </Box>

                {/* Linha 2 - Contato e Endereço */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  {/* Contato */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.contact || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, contact: e.target.value }
                        }
                      })}
                      label={t('deviceContact')}
                    />
                  </Box>

                  {/* Endereço */}
                  <Box flex={2} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.address || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, address: e.target.value }
                        }
                      })}
                      label={t('sharedAddress')}
                    />
                  </Box>
                </Box>

                {/* Linha 3 - Detalhes do Endereço */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  {/* Bairro */}
                  <Box flex={1.5} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.neighborhood || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, neighborhood: e.target.value }
                        }
                      })}
                      label={t('sharedNeighborhood')}
                    />
                  </Box>

                  {/* Cidade */}
                  <Box flex={1.5} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.city || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, city: e.target.value }
                        }
                      })}
                      label={t('sharedCity')}
                    />
                  </Box>

                  {/* Estado */}
                  <Box flex={0.6} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.state || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, state: e.target.value }
                        }
                      })}
                      label={t('sharedState')}
                    />
                  </Box>

                  {/* CEP */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.postCode || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, postCode: e.target.value }
                        }
                      })}
                      label={t('sharedPostalCode')}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <DriverIcon color="white" />
                <Typography variant="subtitle1">
                  {t('sharedDriverLicence')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.details}>
              <Box display="flex" flexDirection="column" gap={3}>
                {/* Linha 1 - Dados da CNH */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  {/* Número da CNH */}
                  <Box flex={2} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.document || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, document: e.target.value }
                        }
                      })}
                      label={t('sharedDocumentCNH')}
                      placeholder="Ex: 12345678900"
                    />
                  </Box>

                  {/* Categoria */}
                  <Box flex={1} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.documentCategory || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, documentCategory: e.target.value }
                        }
                      })}
                      label={t('deviceCategory')}
                      placeholder="Ex: AB"
                    />
                  </Box>
                </Box>

                {/* Linha 2 - Validade e Tipo Sanguíneo */}
                <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                  {/* Validade */}
                  <Box flex={1.5} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.documentValidAt || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, documentValidAt: e.target.value }
                        }
                      })}
                      label={t('sharedDocumentValidDate')}
                      type="date"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  {/* Tipo Sanguíneo */}
                  <Box flex={0.5} minWidth={0}>
                    <TextField
                      fullWidth
                      value={item?.attributes?.form?.bloodType || ''}
                      onChange={(e) => setItem({
                        ...item,
                        attributes: {
                          ...item.attributes,
                          form: { ...item.attributes.form, bloodType: e.target.value }
                        }
                      })}
                      label={t('sharedBloodType')}
                      placeholder="Ex: O+"
                    />
                  </Box>
                </Box>

                {/* Seção para upload de imagem da CNH (opcional) */}
                {item.id && (
                  <Box mt={2}>
                    <MuiFileInput
                      fullWidth
                      label={t('attributeDl')}
                      placeholder="Clique aqui para anexar Copia da CNH"
                      value={null}
                      onChange={(file) => console.log('Implementar upload', file)}
                      inputProps={{ accept: 'image/*,.pdf' }}
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <EditAttributesAccordion
            attributes={filterAttributes(item.attributes)}
            setAttributes={(attributes) => setItem({ ...item, attributes: { ...item.attributes, ...attributes } })}
            definitions={{}}
          />
        </>
      )}
    </EditItemView>
  );
};

export default DriverPage;
